import { COLUMN_NAME_ATTR } from '../../constants';
import { TourTarget } from '../../types/guides';

export const CONTROL_KEY_ATTR = 'data-control-key';
export const FUNCTIONAL_KEY_ATTR = 'data-functional-key';
export const MENU_KEY_ATTR = 'data-menu-url';
export const FORM_FIELD_ATTR = 'data-form-field';
export const FORM_SECTION_ATTR = 'data-form-section';

const escapeAttributeValue = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');

/** CSS selector for a tour target, or null for anchor-less (page) steps. */
export const selectorForGuideTarget = (target: TourTarget): string | null => {
  switch (target.kind) {
    case 'menu':
      return target.key
        ? `[${MENU_KEY_ATTR}="${escapeAttributeValue(target.key)}"]`
        : null;
    case 'control':
      return target.name
        ? `[${CONTROL_KEY_ATTR}="${escapeAttributeValue(target.name)}"]`
        : null;
    case 'functional':
      return target.key
        ? `[${FUNCTIONAL_KEY_ATTR}="${escapeAttributeValue(target.key)}"]`
        : null;
    case 'section':
      return target.name
        ? `[${FORM_SECTION_ATTR}="${escapeAttributeValue(target.name)}"]`
        : null;
    case 'field':
      return target.name
        ? `[${FORM_FIELD_ATTR}="${escapeAttributeValue(target.name)}"]`
        : null;
    case 'column':
      return target.name
        ? `[${COLUMN_NAME_ATTR}="${escapeAttributeValue(target.name)}"]`
        : null;
    case 'selector':
      return target.selector ?? null;
    case 'page':
    default:
      return null;
  }
};

export const isElementVisible = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) return element.getClientRects().length > 0;
  if (element.hidden) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

/**
 * Finds the DOM element a tour step points at. Prefers a visible match so a
 * hidden mobile menu or an off-screen mirror button never wins over the real one.
 */
export const resolveGuideTarget = (
  target: TourTarget,
  root: ParentNode = document
): HTMLElement | null => {
  const selector = selectorForGuideTarget(target);
  if (!selector) return null;
  let candidates: Element[];
  try {
    candidates = Array.from(root.querySelectorAll(selector));
  } catch {
    return null;
  }
  const visible = candidates.find(isElementVisible);
  const match = visible ?? null;
  return match instanceof HTMLElement ? match : null;
};

/** First matching element even when hidden (inactive tab, collapsed accordion). */
export const findAnyGuideTarget = (
  target: TourTarget,
  root: ParentNode = document
): HTMLElement | null => {
  const selector = selectorForGuideTarget(target);
  if (!selector) return null;
  try {
    const match = root.querySelector(selector);
    return match instanceof HTMLElement ? match : null;
  } catch {
    return null;
  }
};

/**
 * Makes a hidden target visible by opening the UI container that hides it:
 * a PrimeReact TabView panel (form sections) or an Ionic accordion (sidebar
 * blocks / submenus). Returns true when something was opened.
 */
export const revealGuideTarget = (element: HTMLElement): boolean => {
  const tabPanel = element.closest<HTMLElement>('[role="tabpanel"][aria-labelledby]');
  if (tabPanel) {
    const headerId = tabPanel.getAttribute('aria-labelledby');
    const header = headerId ? document.getElementById(headerId) : null;
    if (header && header.getAttribute('aria-selected') !== 'true') {
      header.click();
      return true;
    }
  }
  const accordion = element.closest<HTMLElement>('ion-accordion');
  if (accordion && !accordion.classList.contains('accordion-expanded')) {
    const header = accordion.querySelector<HTMLElement>('[slot="header"]');
    if (header) {
      header.click();
      return true;
    }
  }
  return false;
};

/** Polls until the target renders (route transitions are async) or the timeout passes. */
export const waitForGuideTarget = (
  target: TourTarget,
  options: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<HTMLElement | null> => {
  const timeoutMs = options.timeoutMs ?? 4000;
  const intervalMs = options.intervalMs ?? 120;
  if (target.kind === 'page') return Promise.resolve(null);
  return new Promise((resolvePromise) => {
    const startedAt = Date.now();
    const tick = () => {
      const element = resolveGuideTarget(target);
      if (element) {
        resolvePromise(element);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolvePromise(null);
        return;
      }
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });
};

const waitForAnyGuideTarget = (
  target: TourTarget,
  options: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<HTMLElement | null> => {
  const timeoutMs = options.timeoutMs ?? 6000;
  const intervalMs = options.intervalMs ?? 120;
  return new Promise((resolvePromise) => {
    const startedAt = Date.now();
    const tick = () => {
      const element = findAnyGuideTarget(target);
      if (element) {
        resolvePromise(element);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolvePromise(null);
        return;
      }
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });
};

/** Reveals, scrolls to and briefly highlights a stable target after navigation. */
export const activateGuideTarget = async (
  target: TourTarget,
  options: { timeoutMs?: number; highlightMs?: number } = {}
): Promise<HTMLElement | null> => {
  if (target.kind === 'page') return null;
  let element = resolveGuideTarget(target);
  if (!element) {
    const hiddenOrPending = await waitForAnyGuideTarget(target, options);
    if (!hiddenOrPending) return null;
    revealGuideTarget(hiddenOrPending);
    element = resolveGuideTarget(target) ?? await waitForGuideTarget(target, options);
  }
  if (!element) return null;
  element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  element.classList.add('guide-inline-target-active');
  const hadTabIndex = element.hasAttribute('tabindex');
  if (!hadTabIndex) element.setAttribute('tabindex', '-1');
  element.focus({ preventScroll: true });
  window.setTimeout(
    () => {
      element?.classList.remove('guide-inline-target-active');
      if (!hadTabIndex) element?.removeAttribute('tabindex');
    },
    options.highlightMs ?? 2600
  );
  return element;
};
